import { capitalize } from '@carboncopyco/utils';
import { Tab } from '@headlessui/react';
import type { ReactNode } from 'react';
import { TextBase } from '../Typography';

type SideTabsItem<T extends string | number> = {
  value: T;
  label: string;
  icon: ReactNode;
};

export interface SideTabsProps<T extends string | number> {
  items: SideTabsItem<T>[];
  extraSidebarContent?: ReactNode;
  children: ReactNode;
}

export const SideTabs = <T extends string | number>({
  items,
  extraSidebarContent,
  children,
}: SideTabsProps<T>) => (
  <div className="flex grow min-w-0 min-h-0">
    <Tab.Group vertical>
      <div className="flex flex-col w-60 pt-5">
        <div className="h-full overflow-y-auto">
          <Tab.List>
            <div className="flex flex-col gap-3">
              {items.map(({ value, label, icon }) => {
                return (
                  <Tab
                    key={value}
                    value={value}
                    className="flex items-center hover:cursor-pointer whitespace-nowrap ui-selected:bg-ds-primary-50 focus:bg-ds-natural-50 hover:bg-ds-natural-50 pl-1 border-l-3 border-transparent ui-selected:border-ds-primary-300 focus:outline-none"
                  >
                    {({ selected }) => (
                      <div className="flex items-center px-5 py-3 min-w-0 text-ds-natural-600 ui-selected:text-ds-primary-300">
                        <div className="mr-1.5">{icon}</div>
                        <TextBase
                          ellipsis
                          label={capitalize(label)}
                          color={
                            selected
                              ? 'text-ds-primary-300'
                              : 'text-ds-natural-600'
                          }
                        />
                      </div>
                    )}
                  </Tab>
                );
              })}
            </div>
          </Tab.List>
        </div>
        {extraSidebarContent && (
          <div className="mt-auto">{extraSidebarContent}</div>
        )}
      </div>
      <Tab.Panels className="flex flex-1 min-w-0 border-l border-l-ds-natural-150 h-full w--full">
        {children}
      </Tab.Panels>
    </Tab.Group>
  </div>
);
